import React, { useState, useEffect } from "react";
import {
  Typography,
  CardBody,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Checkbox,
  Select as MTSelect,
  Option,
  IconButton,
} from "@material-tailwind/react";
import { IoClose, IoPersonCircle, IoArrowBackSharp } from "react-icons/io5";
import { PiCopyFill } from "react-icons/pi";
import { RiDeleteBin7Fill } from "react-icons/ri";
import Paper from "@mui/material/Paper";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";

import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import { FormControl, TextField, Badge } from "@mui/material";
import { defaultSearchData } from "../../data/filterData";

import { useContactsContext } from "../../contexts/ContactsContextProvider";
import { useUserContext } from "../../contexts/UserContextProvider";
import {
  isoTimestampToReadable,
  getCurrentDateTime,
} from "../utils/timeStampConversion";
import { loadMoreContact } from "../../data/messageNotifierServices";
import { CreateContactGroup } from "..";

const ManageContactGroups = ({ drawerStatus, callbackHideContactGroup }) => {
  const {
    contactGroups,
    selectedGroupContacts,
    apiCallDeleteContactGroup,
    apiCallFetchGroupContacts,
    apiCallRemoveContactsFromGroup,
    apiCallContactsInsights,
    apiCallFetchContactGroups,
    apiCallCopyContactFromGroup,
  } = useContactsContext();
  const { currentUserInfo } = useUserContext();

  const [showSelectedGroup, setShowSelectedGroup] = useState(false);
  const [selectedGroupToView, setSelectedGroupToView] = useState("");
  const [openDeleteGroupDialog, setOpenDeleteGroupDialog] = useState(false);
  const [contactsSelected, setContactsSelected] = useState([]);
  const [searchContact, setSearchContact] = useState(defaultSearchData);
  const [openGroupCreationDialog, setOpenGroupCreationDialog] = useState(false);
  const [showCopyContactDialog, setShowCopyContactDialog] = useState(false);
  const [targetContactGroup, setTargetContactGroup] = useState("");
  const [totalSelectedCount, setTotalSelectedCount] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [hasFilter, setHasFilter] = useState(null);

  const handleFilterOnChange = (field) => (event) => {
    if (
      field === "waOptIn" ||
      field === "isVerifiedNo" ||
      field === "isBlocked"
    ) {
      setSearchContact({
        ...searchContact,
        [field]: event ?? event.target.checked,
      });
    } else {
      setSearchContact({
        ...searchContact,
        [field]: event.target.value ?? event.target.checked,
      });
    }
  };

  const selectedGroupContactsColumns = [
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          checked={selectAll}
          indeterminate={
            contactsSelected.length > 0 &&
            contactsSelected.length < selectedGroupContacts.length
          }
          onChange={handleSelectAllOnChange}
          title="Select All"
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={contactsSelected.includes(
            JSON.stringify({
              countryCode: params?.row?.countryCode,
              phoneNo: params?.row?.phoneNo,
            })
          )}
          onChange={(e) =>
            selectMultipleContactsOnClick(
              e,
              JSON.stringify({
                countryCode: params?.row?.countryCode,
                phoneNo: params?.row?.phoneNo,
              })
            )
          }
        />
      ),
    },
    {
      field: "index",
      headerName: "S.No",
      width: 70,
      sortable: false,
      valueGetter: (value, row) => {
        const index = selectedGroupContacts.findIndex(
          (contact) => contact?.contactId === row?.contactId
        );
        return index + 1;
      },
    },
    {
      field: "name",
      headerName: "Name",
      width: 400,
      sortable: true,
      valueGetter: (value, row) => `${row?.firstName} ${row?.lastName}`,
      renderCell: (params) => (
        <div className="flex items-center gap-3">
          <IoPersonCircle size={20} className="text-slate-800" />
          <span className="font-bold">{params.value}</span>
        </div>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => `+${row?.countryCode} ${row?.phoneNo}`,
    },
    {
      field: "lastContacted",
      headerName: "Last Contacted",
      width: 200,
      sortable: true,
      valueGetter: (value, row) =>
        row?.lastContacted ? isoTimestampToReadable(row.lastContacted) : "-",
    },
    {
      field: "email",
      headerName: "Email",
      width: 300,
      sortable: true,
      valueGetter: (value, row) => row?.email || "-",
    },
    {
      field: "isVerfiedNumber",
      headerName: "Is Verified No.?",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (row?.isVerfiedNumber === 1) return "Yes";
        else if (row?.isVerfiedNumber === 0) return "No";
        return "-";
      },
    },
    {
      field: "waOptIn",
      headerName: "WA Opt In?",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (row?.waOptIn === 2)
          return "Yes"; // Based on your data, 2 seems to be opted in
        else if (row?.waOptIn === 1) return "Yes";
        else if (row?.waOptIn === 0) return "No";
        return "-";
      },
    },
    {
      field: "isBlocked",
      headerName: "Is Blocked?",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => row?.isBlocked || "-",
    },
  ];

  const contactGroupColumns = [
    {
      field: "groupName",
      headerName: "Group Name",
      width: 400,
      sortable: true,
    },
    { field: "numberOfContacts", headerName: "No. of Contacts", width: 150 },
    {
      field: "noOfTimesMessageSent",
      headerName: "Times messages sent",
      width: 200,
    },
    { field: "notInterested", headerName: "# Not Interested", width: 150 },
    {
      field: "groupDescription",
      headerName: "Group Description",
      width: 400,
      sortable: false,
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={(e) => {
            setSelectedGroupToView(params?.row);
            handleGroupToViewOnClick(e, params?.row?.groupName);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const resetStates = () => {
    setShowSelectedGroup(false);
    setOpenDeleteGroupDialog(false);
    setSelectedGroupToView("");
    setContactsSelected([]);
    setSearchContact(defaultSearchData);
    setOpenGroupCreationDialog();
  };

  const handleGroupToViewOnClick = (event, selectedGroup) => {
    event.preventDefault();
    if (selectedGroup !== "") {
      apiCallFetchGroupContacts(selectedGroup, searchContact);
      setShowSelectedGroup(true);
    }
  };

  const selectMultipleContactsOnClick = (event, selectedData) => {
    const isChecked = event.target.checked;

    setContactsSelected((prevSelectedContacts) => {
      const updatedSelectedContacts = isChecked
        ? [...prevSelectedContacts, selectedData]
        : prevSelectedContacts.filter((data) => data !== selectedData);
      setTotalSelectedCount(updatedSelectedContacts?.length);
      setSelectAll(
        updatedSelectedContacts.length === selectedGroupContacts.length
      );
      return updatedSelectedContacts;
    });
  };

  const handleSelectAllOnChange = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    const selectedContact = isChecked
      ? selectedGroupContacts.map((contact) => {
          return JSON.stringify({
            countryCode: contact?.countryCode,
            phoneNo: contact?.phoneNo,
          });
        })
      : [];
    setContactsSelected(selectedContact);
    setTotalSelectedCount(selectedContact?.length);
  };

  const removeContactsFromGroupOnClick = async (event) => {
    event.preventDefault();

    if (
      contactGroups.filter(
        (x) => x.groupName === selectedGroupToView?.groupName
      )
    ) {
      apiCallRemoveContactsFromGroup(
        contactsSelected,
        selectedGroupToView?.groupName
      );
    }

    setContactsSelected(() => []);
  };

  const handelCopyContact = async (event) => {
    event.preventDefault();
    const fromGroup = selectedGroupToView.groupName;
    const toGroup = targetContactGroup;
    await apiCallCopyContactFromGroup(fromGroup, toGroup, contactsSelected);
    setShowCopyContactDialog(false);
    setTargetContactGroup("");
  };

  const handleDeleteOnClick = async (groupNameToBeDeleted) => {
    const resp = await apiCallDeleteContactGroup(groupNameToBeDeleted);
    if (resp) {
      resetStates();
      apiCallContactsInsights();
      await apiCallFetchContactGroups();
    }
  };

  const downloadContactsInGroupOnClick = async () => {
    const mappedContacts = selectedGroupContacts.reduce((acc, contact) => {
      acc.push({
        first_name: contact?.firstName,
        last_name: contact?.lastName,
        country_code: contact?.countryCode,
        phone_number: contact?.phoneNo,
        email: contact?.email,
        last_contacted: isoTimestampToReadable(contact?.lastContacted),
        is_verfied_number: contact?.isVerfiedNumber,
        wa_opt_in: contact?.waOptIn,
        is_blocked: contact?.isBlocked,
      });
      return acc;
    }, []);

    const worksheet = XLSX.utils.json_to_sheet(mappedContacts);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `Group Contacts ${getCurrentDateTime()}.xlsx`);
  };

  const handleScroll = (e) => {
    // Prevent scrolling from bubbling up to parent elements
    e.stopPropagation();
  };

  useEffect(() => {
    if (!drawerStatus) resetStates();
  }, [drawerStatus]);

  return (
    <>
      {showSelectedGroup ? (
        <div className="h-full w-full flex flex-col justify-between">
          <div className="space-y-5">
            <div className="flex justify-between items-center gap-1">
              <span className="flex items-center gap-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowSelectedGroup(false);
                    setContactsSelected([]);
                  }}
                >
                  <IoArrowBackSharp
                    size={20}
                    className="my-smooth-transition-1 text-wabmBlue hover:text-wabmGreen"
                  />
                </button>
                <Typography variant="h5" color="blue-gray">
                  {selectedGroupToView?.groupName}
                </Typography>
              </span>
              <div className="flex items-center gap-5 text-sm">
                <span>
                  <span>Selected: </span>
                  <span>{totalSelectedCount}</span>
                </span>
                |
                <span>
                  <span>Max Contacts allowed in a Group: </span>
                  <span>{currentUserInfo?.maxContactsInGroup}</span>
                </span>
              </div>
              <div className="flex flex-row justify-end gap-2">
                <Badge
                  color="error"
                  variant="dot"
                  sx={{
                    "& .MuiBadge-dot": {
                      height: "14px",
                      minWidth: "14px",
                      borderRadius: "7px",
                    },
                  }}
                  invisible={!hasFilter}
                  overlap="circular"
                >
                  <IconButton
                    title="Filter Contact"
                    className="bg-blue-600"
                    onClick={() => setShowFilter(!showFilter)}
                  >
                    <FilterListIcon />
                  </IconButton>
                </Badge>
                {currentUserInfo?.isAdmin === 2 && (
                  <IconButton
                    className="bg-green-600"
                    title="Download Group Contacts"
                    onClick={() => downloadContactsInGroupOnClick()}
                  >
                    <DownloadIcon />
                  </IconButton>
                )}
              </div>
            </div>
            <CardBody onScroll={handleScroll} className="max-h-[70vh] p-0">
              {selectedGroupContacts?.length > 0 ? (
                <Paper style={{ position: "relative", zIndex: 1, height: 500 }}>
                  <DataGrid
                    getRowId={(row) => row?.contactId}
                    rows={selectedGroupContacts}
                    columns={selectedGroupContactsColumns}
                    sx={{ border: 0 }}
                    getRowClassName={() => "cursor-pointer"}
                    disableRowSelectionOnClick={true}
                  />
                </Paper>
              ) : (
                <p className="pt-3 pl-4 text-sm italic">
                  This contact group does not have any contact numbers yet. You
                  can add some from <strong>All Contacts</strong>.
                </p>
              )}
            </CardBody>
          </div>
          <div className="my-card-style-1 flex justify-between gap-3">
            {contactsSelected?.length === 0 && (
              <span className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="gradient"
                  color="blue"
                  size="sm"
                  onClick={async (e) => {
                    loadMoreContact.next({
                      groupName: selectedGroupToView.groupName,
                      contactInGroup: selectedGroupContacts,
                      drawerStatus: true,
                    });
                  }}
                  className="inline-flex items-center gap-2"
                >
                  <PiCopyFill size="16px" />
                  Add Contacts in this Group
                </Button>
                <Button
                  type="button"
                  variant="gradient"
                  color="red"
                  size="sm"
                  onClick={() =>
                    setOpenDeleteGroupDialog(!openDeleteGroupDialog)
                  }
                  className="inline-flex items-center gap-2"
                >
                  <RiDeleteBin7Fill size="16px" />
                  Delete this Group
                </Button>
              </span>
            )}
            {contactsSelected?.length > 0 && (
              <span className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="gradient"
                  color="blue"
                  size="sm"
                  onClick={async (e) => {
                    await apiCallFetchContactGroups();
                    setShowCopyContactDialog(true);
                  }}
                  className="inline-flex items-center gap-2"
                >
                  <PiCopyFill size="16px" />
                  Copy contacts to another group
                </Button>
                <Button
                  type="button"
                  variant="gradient"
                  color="red"
                  size="sm"
                  onClick={removeContactsFromGroupOnClick}
                >
                  Remove selected contacts from this group
                </Button>
              </span>
            )}
            {contactsSelected?.length > 0 && (
              <Button
                type="button"
                color="red"
                size="sm"
                onClick={() => setContactsSelected([])}
                className="mr-1"
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="h-full w-full space-y-5">
          <div className="flex justify-between items-center gap-5">
            <div>
              <Typography variant="h5" color="blue-gray">
                Manage Contact Groups
              </Typography>
              <p className="mt-1">
                These are details about all your saved contact groups
              </p>
            </div>

            <div className="flex items-center gap-5">
              <Button
                color="blue"
                size="sm"
                onClick={() => setOpenGroupCreationDialog(true)}
                className="w-48 whitespace-nowrap"
              >
                Create New Group
              </Button>
              <button
                onClick={() => callbackHideContactGroup()}
                className="my-button-style-cancel"
              >
                <IoClose size={18} />
              </button>
            </div>
          </div>
          <CardBody
            onScroll={handleScroll}
            style={{ height: "77vh" }}
            className="p-0"
          >
            {contactGroups.length > 0 ? (
              <Paper style={{ position: "relative", zIndex: 1, height: 550 }}>
                <DataGrid
                  getRowId={(row) => row?.groupId}
                  rows={contactGroups}
                  columns={contactGroupColumns}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                  sx={{ border: 0 }}
                  getRowClassName={() => "cursor-pointer"}
                  disableRowSelectionOnClick={true}
                />
              </Paper>
            ) : (
              <p className="pt-3 pl-4 text-sm italic">
                You don't have any contact groups yet. Create one to simplify
                your Campaign Management.
              </p>
            )}
          </CardBody>
        </div>
      )}

      <Dialog
        open={openDeleteGroupDialog}
        handler={() => setOpenDeleteGroupDialog(!openDeleteGroupDialog)}
      >
        <DialogHeader>Delete Contact Group</DialogHeader>
        <DialogBody>
          Do you want to delete the contact group{" "}
          <strong>"{selectedGroupToView?.groupName}"</strong>? Please note that
          this action is irreversible but the contacts in this group will still
          be present in <strong>All Contacts</strong>.
        </DialogBody>
        <DialogFooter>
          <Button
            variant="text"
            color="red"
            size="sm"
            onClick={() => setOpenDeleteGroupDialog(!openDeleteGroupDialog)}
            className="mr-1"
          >
            <span>Cancel</span>
          </Button>
          <Button
            variant="gradient"
            color="green"
            size="sm"
            onClick={() => handleDeleteOnClick(selectedGroupToView?.groupName)}
          >
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={openGroupCreationDialog}
        handler={() => setOpenGroupCreationDialog(!openGroupCreationDialog)}
      >
        <span className="flex justify-between gap-3">
          <DialogHeader>Create Contact Group</DialogHeader>
        </span>
        <DialogBody>
          <CreateContactGroup
            callbackHideContactGroup={() => {
              setOpenGroupCreationDialog(!openGroupCreationDialog);
            }}
          />
        </DialogBody>
      </Dialog>
      <Dialog
        open={showCopyContactDialog}
        handler={() => setShowCopyContactDialog(!showCopyContactDialog)}
      >
        <span className="flex justify-between gap-3">
          <DialogHeader>
            <div className="text-md">
              <p>Select group to copy contact from</p>
              <p className="text-lg text-blue-700">
                {selectedGroupToView.groupName}
              </p>
            </div>
          </DialogHeader>
        </span>
        <form onSubmit={handelCopyContact}>
          <DialogBody>
            <MTSelect
              variant="outlined"
              label="Select Contact Group"
              color="blue"
              required
              value={targetContactGroup}
              onChange={(e) => setTargetContactGroup(e)}
            >
              {contactGroups
                .filter((x) => x.groupName !== selectedGroupToView.groupName)
                ?.map((group, index) => (
                  <Option
                    key={index}
                    value={group?.groupName}
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    {group?.groupName} (
                    {group?.numberOfContacts ? group.numberOfContacts : 0}{" "}
                    contacts)
                  </Option>
                ))}
            </MTSelect>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="text"
              color="red"
              size="sm"
              onClick={() => setShowCopyContactDialog(false)}
              className="mr-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="blue"
              size="sm"
              className="w-48 whitespace-nowrap"
            >
              Copy Contact to Group
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
      <Dialog
        open={showFilter}
        handler={() => setShowFilter(!showFilter)}
        className="w-36"
      >
        <DialogHeader className="flex items-center gap-2 text-blue-600 text-xl">
          <FilterListIcon className="text-blue-600" />
          <Typography variant="h5" className="font-semibold">
            Filter Contact
          </Typography>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="flex flex-col justify-start">
            <div className="flex flex-row justify-evenly gap-2">
              <FormControl fullWidth margin="normal" className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.isVerifiedNo.toString()}
                  onChange={handleFilterOnChange("isVerifiedNo")}
                  label="Is Verified"
                >
                  <Option
                    value="-1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    All
                  </Option>
                  <Option
                    value="1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    Yes
                  </Option>
                  <Option
                    value="0"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    No
                  </Option>
                </MTSelect>
              </FormControl>

              <FormControl fullWidth margin="normal" className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.waOptIn.toString()}
                  onChange={handleFilterOnChange("waOptIn")}
                  label="WA Opt In"
                >
                  <Option
                    value="-1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    All
                  </Option>
                  <Option
                    value="1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    Yes
                  </Option>
                  <Option
                    value="0"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    No
                  </Option>
                </MTSelect>
              </FormControl>
              <FormControl fullWidth margin="normal" className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.isBlocked.toString()}
                  onChange={handleFilterOnChange("isBlocked")}
                  label="Is Blocked"
                >
                  <Option
                    value="-1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    All
                  </Option>
                  <Option
                    value="1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    Yes
                  </Option>
                  <Option
                    value="0"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    No
                  </Option>
                </MTSelect>
              </FormControl>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                size="small"
                sx={{ mt: 0, mb: 1 }}
                value={searchContact?.name}
                onChange={handleFilterOnChange("name")}
              />
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                size="small"
                sx={{ mt: 0, mb: 1 }}
                value={searchContact?.phoneNumber}
                onChange={handleFilterOnChange("phoneNumber")}
              />
            </div>

            <div className="flex flex-row justify-end gap-2">
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setSearchContact(defaultSearchData);
                }}
              >
                Clear Filter
              </Button>
              <Button
                color="blue"
                variant="contained"
                onClick={(e) => {
                  const isApplied = Object.keys(searchContact).some(
                    (key) => searchContact[key] !== defaultSearchData[key]
                  );
                  setHasFilter(isApplied);
                  handleGroupToViewOnClick(e, selectedGroupToView?.groupName);
                  setShowFilter(false);
                }}
              >
                Apply
              </Button>
              <Button
                color="red"
                variant="contained"
                onClick={() => setShowFilter(!showFilter)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};

export default ManageContactGroups;
